/**
 * MuxPosterBackdrop — friendly progressive "blur-up" backdrop for a Mux video.
 *
 * Why this exists: a single full-size Mux thumbnail set as a CSS background
 * makes the user stare at a flat black screen + spinner until the whole
 * ~30-80 KB WebP downloads (painful on 3G / Instagram in-app browser).
 *
 * Strategy (industry-standard blur-up):
 *   1. Tasteful dark gradient base   → never a raw black flash, paints instantly.
 *   2. LQIP (≈40 px, q30, ~1-2 KB)   → blurred via CSS, lands almost immediately,
 *                                       gives instant colour + composition context.
 *   3. Full poster (device-sized)    → fades in over the LQIP once decoded.
 *   4. onReady() fires when the full  → lets the parent retire its spinner as soon
 *      poster is painted                as there is a real frame on screen.
 *
 * The LQIP <link rel="preload"> is injected as soon as the playbackId is known so
 * the browser fetches it in parallel with the JS that mounts the player. No
 * crossOrigin is set (image.mux.com doesn't need it; setting it breaks the hint).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { isMuxVideoDisplayEnabled } from "@/lib/muxDisplayEnabled";
import { cn } from "@/lib/utils";

export interface MuxPosterBackdropProps {
  /** Mux playback ID. When empty/undefined only the gradient base renders. */
  playbackId?: string | null;
  /** Frame time (seconds) for the thumbnail. Default 0 (first frame). */
  time?: number;
  /** Mux image fit mode. "smartcrop" fills (reels); "preserve" letterboxes. */
  fitMode?: "smartcrop" | "preserve";
  /** Fired once the full-resolution poster has decoded and is visible. */
  onReady?: () => void;
  /** Hide everything (e.g. when the video is already playing) with a fade. */
  hidden?: boolean;
  className?: string;
}

/** Round viewport width to a CDN-cache-friendly bucket (avoids per-px cache misses). */
function posterWidthBucket(): number {
  if (typeof window === "undefined") return 1080;
  const w = window.innerWidth * (window.devicePixelRatio || 1);
  if (w <= 480) return 480;
  if (w <= 720) return 720;
  if (w <= 1080) return 1080;
  return 1440;
}

export default function MuxPosterBackdrop({
  playbackId,
  time = 0,
  fitMode = "smartcrop",
  onReady,
  hidden = false,
  className,
}: MuxPosterBackdropProps) {
  // When Mux display is off, skip image.mux.com posters entirely.
  const pid = isMuxVideoDisplayEnabled() ? playbackId?.trim() || "" : "";

  const lqipUrl = useMemo(
    () =>
      pid
        ? `https://image.mux.com/${pid}/thumbnail.webp?time=${time}&width=40&quality=30&fit_mode=${fitMode}`
        : null,
    [pid, time, fitMode],
  );

  const fullUrl = useMemo(
    () =>
      pid
        ? `https://image.mux.com/${pid}/thumbnail.webp?time=${time}&width=${posterWidthBucket()}&quality=72&fit_mode=${fitMode}`
        : null,
    [pid, time, fitMode],
  );

  const [fullLoaded, setFullLoaded] = useState(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Preload the LQIP at high priority the moment we know the playback ID.
  useEffect(() => {
    if (!lqipUrl || typeof document === "undefined") return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = lqipUrl;
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [lqipUrl]);

  // Decode the full poster off-DOM; flip to "loaded" only once it can paint
  // without jank. Falls back gracefully if decode() is unsupported.
  useEffect(() => {
    if (!fullUrl) return;
    let cancelled = false;
    const img = new Image();
    img.src = fullUrl;
    const markReady = () => {
      if (cancelled) return;
      setFullLoaded(true);
      onReadyRef.current?.();
    };
    if (typeof img.decode === "function") {
      img.decode().then(markReady, markReady);
    } else {
      img.onload = markReady;
      img.onerror = markReady;
    }
    return () => {
      cancelled = true;
    };
  }, [fullUrl]);

  return (
    <div
      aria-hidden
      className={cn(
        "absolute inset-0 overflow-hidden transition-opacity duration-500 ease-out",
        hidden ? "opacity-0" : "opacity-100",
        className,
      )}
    >
      {/* 1. Instant base — a calm dark gradient, never a raw black flash. */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-zinc-900/50" />

      {/* 2. LQIP — blurred, slightly over-scaled so blur edges never show. */}
      {lqipUrl && (
        <img
          src={lqipUrl}
          alt=""
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl"
          style={{ willChange: "opacity" }}
        />
      )}

      {/* 3. Full poster — fades in over the LQIP once decoded. */}
      {fullUrl && (
        <img
          src={fullUrl}
          alt=""
          decoding="async"
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out",
            fullLoaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </div>
  );
}
